import { StandaloneArticleDateProps } from '@/components/ArticleDate';
import { findBylines, StandaloneArticleHeaderProps } from '@/components/ArticleHeader';
import { ArticleHeader } from '@/components/ArticleHeader/ArticleHeader';
import { BYLINE_TYPE, StandaloneBylineProps } from '@/components/Byline';
import ErrorBoundary from '@/components/ErrorBoundary';
import { StandaloneImageProps } from '@/components/Image';
import { StandaloneTagProps } from '@/components/Tag';
import Badge from '@/mabra/components/element/Badge';
import { componentTheme } from '@/styles/ArticleHeader';
import { LabradorComponent, StandaloneComponent } from '@/types/component';
import { useAppState } from 'lib/hooks/useAppState';
import { ArticleBadgeData } from 'lib/labrador/articleBadge';
import { authorNameToSlug } from 'lib/labrador/author-page/slug';
import { cn, withDebugMetadata } from 'lib/utils';
import mergeProps, { mergeOptions } from 'lib/utils/mergeProps';
import { standaloneComponent } from 'modules/dynamic-components';

const ArticleDate = standaloneComponent<StandaloneArticleDateProps>('ElementArticleDate');
const Byline = standaloneComponent<StandaloneBylineProps>('ContentByline');
const Image = standaloneComponent<StandaloneImageProps>('ContentImage');
const Tag = standaloneComponent<StandaloneTagProps>('ElementTag');

export interface MabraStandaloneArticleHeaderProps extends StandaloneArticleHeaderProps {
  badge?: ArticleBadgeData;
}

export const MabraStandaloneArticleHeader: StandaloneComponent<MabraStandaloneArticleHeaderProps> = ({
  bylines,
  category,
  description,
  headline,
  image,
  tags,
  datetime,
  badge,
  options,
  ...props
}) => {
  const {
    $bylines: bylinesProps,
    $date: dateProps,
    $category: categoryProps,
    $description: descriptionProps,
    $headline: headlineProps,
    $image: imageProps,
    $tags: tagsProps,
    variant = 'second',
    ...baseProps
  } = options ?? {};

  const { $tag: $categoryTag, ...$category } = categoryProps ?? {};
  const { $tag, ...$tags } = tagsProps ?? {};

  return (
    <ErrorBoundary>
      <ArticleHeader variant={variant} {...baseProps} {...props}>
        {tags && tags.length > 0 && (
          <ArticleHeader.Tags variant={variant} {...$tags}>
            {tags?.map((tag, index) => {
              return (
                <Tag
                  key={index}
                  link={{ href: `/etikett/${tag.value}` }}
                  {...mergeProps(
                    {
                      options: mergeOptions(
                        {
                          variant: 'text',
                          theme: componentTheme('tag'),
                        },
                        $tag
                      ),
                    },
                    tag
                  )}
                />
              );
            })}
          </ArticleHeader.Tags>
        )}
        {headline && (
          <ArticleHeader.Headline variant={variant} {...headlineProps}>
            {headline}
          </ArticleHeader.Headline>
        )}
        {category?.value && (
          <ArticleHeader.Category variant={variant} {...$category}>
            <Tag
              value={category.value}
              link={{ href: category.url || `etikett/${category.value}` }}
              options={mergeOptions(
                {
                  variant: 'text',
                  theme: componentTheme('tag'),
                },
                $categoryTag
              )}
            />
          </ArticleHeader.Category>
        )}
        {datetime?.publishedDatetime && (
          <ArticleHeader.Date variant={variant} {...dateProps}>
            <ArticleDate {...datetime} />
          </ArticleHeader.Date>
        )}
        {bylines && bylines?.length > 0 && (
          <ArticleHeader.Bylines variant={variant} {...bylinesProps}>
            {bylines?.map((byline, index) => (
              <Byline {...byline} key={index} />
            ))}
          </ArticleHeader.Bylines>
        )}
        {image && (
          <ArticleHeader.Image variant={variant} {...imageProps}>
            <Image
              {...mergeProps(
                {
                  options: {
                    fill: true,
                    priority: true,
                    $caption: {
                      $byline: {
                        className: cn(
                          'text-mb-headline-2xs',
                          '!text-mb-gray-600',
                          'not-italic',
                          'sm:text-right',
                          badge ? 'mt-1' : '-mt-2',
                          'sm:-mt-2'
                        ),
                      },
                      $text: { className: 'h-0 !mb-0' },
                    },
                  },
                },
                image
              )}
              caption={badge && <Badge data={badge} variant="header" />}
            />
          </ArticleHeader.Image>
        )}
        {description && (
          <ArticleHeader.Description variant={variant} {...descriptionProps}>
            {description}
          </ArticleHeader.Description>
        )}
      </ArticleHeader>
    </ErrorBoundary>
  );
};

const MabraLabradorArticleHeader: LabradorComponent = ({ type, data, meta, descendants }) => {
  const [{ device }] = useAppState();

  const {
    format = 'DD MMM, YYYY',
    publishedDatetime,
    updatedDatetime,
    enabledAuthors,
    photoByline,
    extraBylineLabel,
    extraBylineContent,
    badgeData,
  } = data ?? {};

  const labradorImage = descendants?.find(({ type }) => type === 'labradorImage')?.data;

  const { imageUrl, alt, altText, caption, byline, showCaption } = labradorImage ?? {};

  const src = imageUrl?.[device] as string;

  const bylines: StandaloneBylineProps[] = findBylines(descendants).map(({ data: byline, children }) => {
    const { firstName, lastName } = byline ?? {};

    const { imageUrl } = children?.[0]?.data ?? {};

    const author = [firstName, lastName].filter(Boolean).join(' ');

    return {
      type: BYLINE_TYPE.AUTHOR,
      author,
      image: imageUrl?.[device],
      authorPageSlug: enabledAuthors?.includes?.(author) && authorNameToSlug(author),
    };
  });

  if (extraBylineContent) {
    bylines.push({ type: BYLINE_TYPE.FREE, author: extraBylineContent, text: extraBylineLabel });
  }

  return (
    <MabraStandaloneArticleHeader
      headline={data.title}
      description={data.subtitle}
      category={{
        value: data.category,
        url: data.categoryUrl,
      }}
      bylines={bylines}
      image={{
        src,
        alt: alt || altText,
        byline: byline || photoByline,
        caption: showCaption && caption,
        options: { priority: true },
      }}
      datetime={{
        publishedDatetime,
        updatedDatetime,
        format,
      }}
      tags={data.tags?.map((tag: string) => ({
        value: tag,
      }))}
      badge={badgeData}
      data-hostpath={data.hostpath}
      data-category={data.category}
      {...withDebugMetadata({ type, meta })}
    />
  );
};

export const standalone = MabraStandaloneArticleHeader;

export default MabraLabradorArticleHeader;
